import React, { useState, useEffect } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Box, CircularProgress } from '@material-ui/core';
import { LoadScript, GoogleMap, InfoWindow } from '@react-google-maps/api';
import RenderPins from 'routes/Pages/SiteAssessment/components/RenderPins';
import RenderPolygons from 'routes/Pages/SiteAssessment/components/RenderPolygons';
import RenderLines from 'routes/Pages/SiteAssessment/components/RenderLines';
import useMapViewData from '../../CustomHooks/useMapView';
import { calculateLineDistance } from '@jumbo/utils/mapHelpers';

const containerStyle = {
  width: '100%',
  height: '70vh',
};

const defaultCenter = { lat: 53.638755, lng: 7.410353 };

const MapView = ({ open, setOpen, siteAssessmentID }) => {
  const { pins, polygons, lines, samples, samplesData, siteData, isLoading, mapData } = useMapViewData(siteAssessmentID);

  const [center, setCenter] = useState(defaultCenter);
  const [zoom, setZoom] = useState(8);
  const [selected, setSelected] = useState(null);

  // Set map center based on available data
  useEffect(() => {
    if (pins.length > 0) {
      setCenter({ lat: pins[0].latitude, lng: pins[0].longitude });
      setZoom(16);
    } else if (polygons.length > 0) {
      const firstPolyCoord = polygons[0]?.coords?.[0];
      if (firstPolyCoord) {
        setCenter({ lat: firstPolyCoord.lat, lng: firstPolyCoord.lng });
        setZoom(15);
      }
    } else if (open && siteData) {
      // Try to geocode the address
      if (siteData.address1 || siteData.address2) {
        geocodeLocation(`${siteData.address1 || ''} ${siteData.address2 || ''}`);
      }
    }
  }, [pins, polygons, open, siteData]);

  const geocodeLocation = location => {
    if (!window.google || !window.google.maps) return;

    const geocoder = new window.google.maps.Geocoder();
    geocoder.geocode({ address: location }, (results, status) => {
      if (status === 'OK' && results?.[0]) {
        const loc = results[0].geometry.location;
        setCenter({ lat: loc.lat(), lng: loc.lng() });
        setZoom(14);
      }
    });
  };

  const handleClose = () => {
    setOpen(false);
    setSelected(null);
  };

  return (
    <Dialog open={open} maxWidth="xl" onClose={handleClose} disableAutoFocus disableEnforceFocus>
      <DialogTitle>Site Map View</DialogTitle>

      <DialogContent style={{ width: '90vw', height: '80vh' }}>
        {isLoading ? (
          <Box display="flex" justifyContent="center" alignItems="center" height="100%">
            <CircularProgress />
          </Box>
        ) : (
          <Box display="flex" flexDirection="row" height="100%">
            {/* Map Section */}
            <Box flex={1} sx={{ height: '100%' }}>
              <LoadScript googleMapsApiKey="AIzaSyA94J1dEXShLTlOZmIZbmOT31Qyo2g-2OU">
                <GoogleMap
                  mapContainerStyle={containerStyle}
                  center={center}
                  zoom={zoom}
                  options={{
                    mapTypeId: 'hybrid',
                    streetViewControl: false,
                    mapTypeControl: true,
                    rotateControl: false,
                  }}>
                  {/* Info Window for selected feature */}
                  {selected && (
                    <InfoWindow
                      position={{
                        lat: selected.latitude || selected.coords?.[0]?.lat,
                        lng: selected.longitude || selected.coords?.[0]?.lng,
                      }}
                      onCloseClick={() => setSelected(null)}>
                      <div style={{ background: 'white', padding: '8px', borderRadius: '4px' }}>
                        <strong style={{ color: 'black' }}>{selected?.displayName || selected?.mapLabel}</strong>
                        {selected?.displayName && selected.displayName.trim() !== '' && (
                          <p style={{ color: 'gray', margin: '4px 0' }}>{selected.mapLabel}</p>
                        )}
                        {selected?.mapType === 'polygon' && <p style={{ margin: '4px 0' }}>{selected.area} Ha</p>}
                        {selected?.mapType === 'line' && <p style={{ margin: '4px 0' }}>{selected.length} km</p>}
                      </div>
                    </InfoWindow>
                  )}

                  {/* Render Pins */}
                  {pins?.length > 0 && (
                    <RenderPins
                      pins={pins}
                      selected={selected}
                      setSelected={setSelected}
                      setShowPin={() => {}} // Read-only, no edit
                      setCurrentPin={() => {}} // Read-only, no edit
                    />
                  )}

                  {/* Render Polygons */}
                  {polygons?.length > 0 && (
                    <RenderPolygons
                      polygons={polygons}
                      selected={selected}
                      setSelected={setSelected}
                      currentPolygon={null}
                      mode={null}
                    />
                  )}

                  {/* Render Lines */}
                  {lines?.length > 0 && (
                    <RenderLines
                      lines={lines}
                      selected={selected}
                      setSelected={setSelected}
                      calculateLineDistance={calculateLineDistance}
                      currentLine={[]}
                      mode={null}
                    />
                  )}
                </GoogleMap>
              </LoadScript>
            </Box>

            {/* Sample Data Section (Optional) */}
            {samples.length > 0 && (
              <Box
                flex={0.4}
                sx={{
                  overflowY: 'auto',
                  paddingLeft: 2,
                  borderLeft: '1px solid #ddd',
                }}>
                <h3>Sample Points</h3>
                {samples.map((sample, idx) => (
                  <div key={idx} style={{ marginBottom: '8px', padding: '8px', background: '#f5f5f5', borderRadius: '4px' }}>
                    <strong>{sample.sampleName}</strong>
                    <p>Species: {sample.species}</p>
                    <p>DBH: {sample.dbh}</p>
                  </div>
                ))}
              </Box>
            )}
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} variant="contained" color="primary">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default MapView;
